/**
 * Subscribe a component to the session — LV §134, Arch §16.
 *
 * The session lives in a module variable, not in React state, because the access token
 * must not go into web storage and must not sit in a context that a rogue component can
 * read. The cost is that `hasPermission()` is a plain function call: it returns whatever
 * was true at render time and nothing re-renders when the user finally arrives.
 *
 * That produced a race with real consequences. The shell renders as soon as
 * authentication succeeds, while `GET /me` — which carries the permission list — may still
 * be in flight. Any permission-dependent UI rendered in that window stayed hidden: on a
 * slower browser the entire sidebar collapsed to the two entries that need no permission,
 * so a signed-in administrator saw no navigation at all. On a faster one it never
 * happened, which is exactly what makes it worth a subscription rather than a workaround.
 *
 * `useSyncExternalStore` is the primitive for precisely this: an external, mutable store
 * that React has to be told about. Nothing here exposes the token — only the user object
 * the UI already renders.
 */

import { useSyncExternalStore } from "react";

import { getUser, subscribe } from "@/auth/session";
import type { Me } from "@/types/api";

/** Re-renders the caller whenever the session changes. */
export function useSession(): Me | null {
  return useSyncExternalStore(subscribe, getUser, getServerSnapshot);
}

/** No session during server rendering; the app is client-rendered anyway. */
function getServerSnapshot(): Me | null {
  return null;
}
